import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tags, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHero } from "@/components/PageHero";

export default function Nomenclatura() {
  const { user } = useAuth();
  const { tenantId, currentOrg } = useTenant();
  const isConsultant = user?.role === "consultant";
  const [data, setData] = useState({ issues: [], total_documents: 0 });
  const [loading, setLoading] = useState(false);
  const needTenant = isConsultant && !tenantId;

  useEffect(() => {
    if (needTenant) { setData({ issues: [], total_documents: 0 }); return; }
    setLoading(true);
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/naming-consistency${p}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [tenantId, needTenant]);

  return (
    <div className="space-y-6">
      <PageHero label="Controllo qualità" title="Coerenza nomenclatura"
        subtitle={<>Segnala i documenti che usano lo stesso codice logico con formattazioni diverse (es. <strong>MOD-04</strong> vs <strong>MOD. 4</strong>) per {tenantId ? <strong>{currentOrg?.nome}</strong> : "tutti i clienti"}.</>} />

      {needTenant ? (
        <Card className="p-12 text-center border-dashed">
          <Tags className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Seleziona un cliente dal menu in alto per eseguire il controllo.</p>
        </Card>
      ) : loading ? (
        <p className="text-sm text-slate-500">Analisi in corso...</p>
      ) : data.issues.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="naming-ok">
          <CheckCircle2 className="h-10 w-10 text-indigo-500 mx-auto mb-3" />
          <p className="text-slate-700 font-medium">Nomenclatura omogenea</p>
          <p className="text-sm text-slate-500 mt-1">Nessuna incoerenza rilevata su {data.total_documents} documenti.</p>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="naming-issues">
          <Card className="p-4 border-amber-200 bg-amber-50/60 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-900">
              Rilevate <strong>{data.issues.length}</strong> incoerenze di nomenclatura su {data.total_documents} documenti. Uniforma i codici al formato suggerito.
            </p>
          </Card>

          {data.issues.map((iss) => (
            <Card key={iss.key} className="p-5 border-slate-200" data-testid={`naming-issue-${iss.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <Tags className="h-4 w-4 text-blue-700" />
                <span className="text-sm text-slate-500">Codice logico</span>
                <span className="font-mono font-semibold">{iss.key}</span>
                <ArrowRight className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-500">Formato suggerito</span>
                <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 font-mono">{iss.suggested}</Badge>
              </div>
              <div className="space-y-2">
                {iss.variants.map((v) => (
                  <div key={v.codice} className="rounded-md border border-slate-200 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="font-mono">{v.codice}</Badge>
                      <span className="text-xs text-slate-500">{v.count} documento{v.count > 1 ? "i" : ""}</span>
                      {v.codice !== iss.suggested && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">da uniformare</Badge>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {v.documents.map((d) => (
                        <Link key={d.id} to={`/documenti/${d.id}`}
                          className="text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors" title={d.titolo}>
                          {d.titolo}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
